import React, { ReactElement, useState } from 'react'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import GoogleWorkspaceIntegrationSetupModal from 'src/component-modal/GoogleWorkspaceIntegrationSetupModal/GoogleWorkspaceIntegrationSetupModal'
import { useTranslate } from 'src/localization/Trns'
import { QueryError } from 'src/queries/network.utils'
import { GoogleEmailIntegrationResponse } from 'src/queries/organizations/emailIntegrations/emailIntegrations.models'
import { useConnectGoogleEmailIntegration } from 'src/queries/organizations/emailIntegrations/emailIntegrations.queries'

type GoogleEmailIntegrationModalProps = {
  isOpen: boolean
  onClose: () => void
  organizationId: string
  integration: GoogleEmailIntegrationResponse
}

const errorCategory = (error: QueryError): string => {
  switch (error.errorCode) {
    case 'EMAIL_INTEGRATION_DELEGATION_NOT_READY':
      return 'delegation_not_ready'
    case 'EMAIL_INTEGRATION_SUBJECT_NOT_WORKSPACE_USER':
      return 'subject_not_workspace_user'
    case 'EMAIL_INTEGRATION_INVALID_ADMIN_EMAIL':
      return 'invalid_admin_email'
    default:
      return 'provider_unavailable'
  }
}

export default function GoogleEmailIntegrationModal({
  isOpen,
  onClose,
  organizationId,
  integration,
}: GoogleEmailIntegrationModalProps): ReactElement {
  const { analytics } = useAnalyticsContext()
  const translate = useTranslate()
  const [verifyError, setVerifyError] = useState<QueryError | null>(null)
  const [connected, setConnected] = useState(false)
  const connect = useConnectGoogleEmailIntegration({
    onSuccess: () => {
      setVerifyError(null)
      setConnected(true)
      analytics.track('email integration', {
        action: 'connect_succeeded',
        provider: 'GOOGLE',
      })
    },
    onError: (error) => {
      setVerifyError(error)
      analytics.track('email integration', {
        action: 'connect_failed',
        provider: 'GOOGLE',
        errorCategory: errorCategory(error),
      })
    },
  })

  const handleClose = () => {
    setVerifyError(null)
    setConnected(false)
    onClose()
  }

  const verificationMessage = () => {
    switch (verifyError?.errorCode) {
      case 'EMAIL_INTEGRATION_DELEGATION_NOT_READY':
        return translate('Google Workspace delegation is not ready yet.')
      case 'EMAIL_INTEGRATION_SUBJECT_NOT_WORKSPACE_USER':
        return translate(
          'Your work email is not a user in this Google Workspace domain.',
        )
      case 'EMAIL_INTEGRATION_INVALID_ADMIN_EMAIL':
        return translate(
          'Your verified work email is required to verify this connection.',
        )
      default:
        return translate('Gmail could not be verified. Try again later.')
    }
  }

  return (
    <GoogleWorkspaceIntegrationSetupModal
      isOpen={isOpen}
      onClose={handleClose}
      integrationName={translate('Gmail')}
      setup={integration.setup}
      prerequisiteDescription={translate(
        'A Google Workspace super admin must authorize company-wide read-only Gmail access. Employees do not connect their mailboxes individually.',
      )}
      prerequisiteItems={[
        translate('Use your verified company email to verify setup.'),
        translate(
          'This connection prepares Gmail for receipt automation; mailbox search and receipt processing are not enabled by this setup alone.',
        ),
        translate(
          'Extend stores only connection metadata, not email messages or attachments.',
        ),
      ]}
      verificationDescription={translate(
        'After the Google Admin grant is saved, verify read-only Gmail access using your signed-in work email.',
      )}
      successDescription={translate(
        'Gmail is connected for the Workspace domain. Receipt search and processing will be enabled separately.',
      )}
      verifyErrorMessage={verifyError ? verificationMessage() : undefined}
      verifyPending={connect.isPending}
      connected={connected}
      onVerify={() => connect.mutate({ orgId: organizationId })}
      onCopy={() =>
        analytics.track('email integration', {
          action: 'instructions_copied',
          provider: 'GOOGLE',
        })
      }
    />
  )
}
